import { useId, useMemo, useRef, useState } from 'react';
import { casaBusca, classeNaData, hojeIso, ROTULO_CLASSE } from '../dados/catalogo.js';
import type { Animal } from '../dados/fonte.js';
import { IconeBusca } from './Icones.js';
import { Etiqueta } from './ui/index.js';

const MAXIMO = 8;

/**
 * Pesquisa de animal por nome ou registro (combobox acessivel: setas, Enter,
 * Esc). Escolhido, mostra o animal e um botao para trocar.
 */
export function BuscaAnimal({
  rotulo,
  animais,
  valor,
  onEscolher,
  excluir,
  apenas,
  ocultarRotulo = false,
}: {
  rotulo: string;
  /** Todos os animais: a classe de cada um depende dos filhos cadastrados. */
  animais: readonly Animal[];
  valor: Animal | null;
  onEscolher: (animal: Animal | null) => void;
  /** Animal que nao pode aparecer nos resultados (o outro lado da comparacao). */
  excluir?: string | null;
  /** Restringe os resultados (ex.: so animais ja avaliados). */
  apenas?: (animal: Animal) => boolean;
  /** Rotulo so para leitor de tela, quando a pagina ja mostra um titulo visivel. */
  ocultarRotulo?: boolean;
}) {
  const id = useId();
  const entrada = useRef<HTMLInputElement>(null);
  const [termo, setTermo] = useState('');
  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(0);

  const hoje = hojeIso();
  const resultados = useMemo(
    () =>
      animais
        .filter((a) => a.id !== excluir && (!apenas || apenas(a)) && casaBusca(a, termo))
        .sort((x, y) => x.name.localeCompare(y.name, 'pt-BR'))
        .slice(0, MAXIMO),
    [animais, excluir, apenas, termo],
  );

  const escolher = (a: Animal) => {
    onEscolher(a);
    setTermo('');
    setAberto(false);
  };

  if (valor) {
    return (
      <div className="space-y-1.5">
        <span className={ocultarRotulo ? 'sr-only' : 'text-sm font-medium'}>{rotulo}</span>
        <div className="flex items-center justify-between gap-3 rounded-(--radius-controle) border border-borda-forte bg-superficie px-3 py-2">
          <div className="min-w-0">
            <p className="truncate font-serif text-[17px] font-semibold text-marca">{valor.name}</p>
            <p className="numero text-xs text-apagado">
              {valor.abccmm_registry ?? 'Sem registro'} · {ROTULO_CLASSE[classeNaData(valor, animais, hoje)]}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              onEscolher(null);
              // Depois de limpar, o foco vai direto para a pesquisa.
              requestAnimationFrame(() => entrada.current?.focus());
            }}
            className="shrink-0 rounded-(--radius-controle) border border-borda-forte px-3 text-[13px] font-medium hover:border-destaque"
          >
            Trocar
          </button>
        </div>
      </div>
    );
  }

  const lista = `${id}-lista`;
  const opcao = (i: number) => `${id}-op-${i}`;

  return (
    <div className="relative space-y-1.5">
      <label htmlFor={`${id}-entrada`} className={ocultarRotulo ? 'sr-only' : 'text-sm font-medium'}>
        {rotulo}
      </label>
      <div className="relative">
        <IconeBusca className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-apagado" />
        <input
          ref={entrada}
          id={`${id}-entrada`}
          type="search"
          role="combobox"
          aria-expanded={aberto}
          aria-controls={lista}
          aria-autocomplete="list"
          aria-activedescendant={aberto && resultados[ativo] ? opcao(ativo) : undefined}
          autoComplete="off"
          placeholder="Pesquisar nome ou registro"
          value={termo}
          onChange={(e) => {
            setTermo(e.target.value);
            setAtivo(0);
            setAberto(true);
          }}
          onFocus={() => setAberto(true)}
          onBlur={() => setAberto(false)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setAberto(true);
              setAtivo((i) => Math.min(i + 1, resultados.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setAtivo((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter' && aberto && resultados[ativo]) {
              e.preventDefault();
              escolher(resultados[ativo]);
            } else if (e.key === 'Escape') {
              setAberto(false);
            }
          }}
          className="w-full rounded-(--radius-controle) border border-borda-forte bg-superficie py-2 pr-3 pl-10 outline-none placeholder:text-apagado focus:border-destaque"
        />
      </div>

      {aberto && (
        <ul
          id={lista}
          role="listbox"
          aria-label={rotulo}
          className="absolute inset-x-0 top-full z-20 mt-1 max-h-80 overflow-auto rounded-(--radius-painel) border border-borda bg-superficie py-1 shadow-menu"
        >
          {resultados.length === 0 ? (
            <li className="px-3 py-3 text-sm text-apagado">Nenhum animal encontrado.</li>
          ) : (
            resultados.map((a, i) => (
              <li
                key={a.id}
                id={opcao(i)}
                role="option"
                aria-selected={i === ativo}
                // mousedown, e nao click: o blur do campo fecharia a lista antes do click.
                onMouseDown={(e) => {
                  e.preventDefault();
                  escolher(a);
                }}
                onMouseEnter={() => setAtivo(i)}
                className={`flex min-h-11 cursor-pointer items-center justify-between gap-3 px-3 py-2 ${
                  i === ativo ? 'bg-destaque-fundo' : ''
                }`}
              >
                <span className="min-w-0">
                  <span className="block truncate font-serif font-semibold">{a.name}</span>
                  <span className="block font-mono text-xs text-apagado">{a.abccmm_registry ?? 'Sem registro'}</span>
                </span>
                <Etiqueta>{ROTULO_CLASSE[classeNaData(a, animais, hoje)]}</Etiqueta>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
